import React, { FC, Suspense, useMemo } from 'react';
import { Box, Text, useSideSheetState, Spinner, SpinnerSize } from '@bechtel/gds';
import { ErrorBoundary, SuspenseLoader } from '@bechtel/client-common';
import { CrewHeaderProps } from './types';
import {
  CrewTimesheetStatusStyled,
  CrewHeaderContainerStyled,
  CrewHeadertyled,
  HeaderButtonStyled,
  DividerStyled,
} from './styled';
import { Plural, Trans } from '@lingui/macro';
import { TimesheetCrewMember } from './crew-member';
import { BulkTimesheet } from '~/detail/bulk-time';
import { useFormikContext } from 'formik';
import { isDirty } from '~/detail/utils';
import { TimesheetFormikState } from '~/detail/types';
import { useTimesheet } from '~/detail/context';

export const CrewHeader: FC<CrewHeaderProps> = ({ crew, timesheetStatus, timesheetParsedValues, savingTimesheet }) => {
  const { visible, open, close } = useSideSheetState(false);
  const { foreman, generalForeman, superintendent, crewEmployees, crewNumber } = crew;
  const crewMemberCount = crewEmployees?.length ? crewEmployees?.length : 0;
  const { isValid, handleSubmit, values, initialValues } = useFormikContext<TimesheetFormikState>();
  const { isEditable } = useTimesheet();

  const isFormDirty = useMemo(() => {
    return isDirty(initialValues, values);
  }, [values, isDirty]);
  return (
    <CrewHeaderContainerStyled>
      <CrewHeadertyled>
        <CrewTimesheetStatusStyled status={timesheetStatus} />
        <Box flexGrow={{ sm: 1 }} p={{ sm: '500' }} justifyContent={{ sm: 'space-between' }}>
          <Box>
            <Box flexDirection={{ sm: 'column' }}>
              <Box mb={{ sm: '300' }}>
                <Text family="graphik" size="600" weight="600" color="white">
                  {crewNumber}
                </Text>
              </Box>
              {crewMemberCount > 0 && (
                <Text size="300" color="white">
                  <Plural
                    value={crewMemberCount}
                    one={`${crewMemberCount} Crew Member`}
                    other={`${crewMemberCount} Crew Members`}
                  />
                </Text>
              )}
            </Box>
            {foreman && (
              <>
                <DividerStyled />
                <TimesheetCrewMember crewMember={foreman} />
              </>
            )}
            {generalForeman && (
              <>
                <DividerStyled />
                <TimesheetCrewMember crewMember={generalForeman} />
              </>
            )}
            {superintendent && (
              <>
                <DividerStyled />
                <TimesheetCrewMember crewMember={superintendent} />
              </>
            )}
          </Box>
          <Box>
            <Box mr={{ sm: '400' }}>
              <HeaderButtonStyled onClick={open} disabled={!isEditable}>
                <Trans>Bulk Time Entry</Trans>
              </HeaderButtonStyled>
            </Box>
            <HeaderButtonStyled
              disabled={!isEditable || !isFormDirty || !isValid}
              onClick={() => handleSubmit()}
              style={{
                minWidth: '120px',
              }}
            >
              {savingTimesheet && (
                <>
                  <Spinner size={SpinnerSize.MEDIUM} />
                  &nbsp;&nbsp;
                </>
              )}
              <Trans>Save</Trans>
            </HeaderButtonStyled>
          </Box>
        </Box>
        {visible && (
          <ErrorBoundary>
            <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
              <BulkTimesheet onClose={close} timesheetParsedValues={timesheetParsedValues} />
            </Suspense>
          </ErrorBoundary>
        )}
      </CrewHeadertyled>
    </CrewHeaderContainerStyled>
  );
};
